import React from 'react';
import { AlertCircle, Clock, CheckCircle2, BellOff } from 'lucide-react';
import { cn } from '../../lib/utils';

interface Props {
  overdue: number;
  upcoming: number;
  completed: number;
  snoozed: number;
  activeFilter: string;
  onFilterChange: (f: string) => void;
}

const cards = [
  {
    key: 'overdue',
    label: 'Overdue',
    icon: AlertCircle,
    bg: 'bg-red-50 dark:bg-red-950/20',
    border: 'border-red-200 dark:border-red-900',
    text: 'text-red-700 dark:text-red-400',
    activeBg: 'bg-red-600 dark:bg-red-700',
    activeText: 'text-white',
  },
  {
    key: 'upcoming',
    label: 'Upcoming',
    icon: Clock,
    bg: 'bg-[hsl(var(--brand-primary)/0.07)] dark:bg-[hsl(var(--brand-primary)/0.12)]',
    border: 'border-[hsl(var(--brand-primary)/0.25)] dark:border-[hsl(var(--brand-primary)/0.3)]',
    text: 'text-[hsl(var(--brand-primary))]',
    activeBg: 'bg-[hsl(var(--brand-primary))]',
    activeText: 'text-white',
  },
  {
    key: 'completed',
    label: 'Completed',
    icon: CheckCircle2,
    bg: 'bg-green-50 dark:bg-green-950/20',
    border: 'border-green-200 dark:border-green-900',
    text: 'text-green-700 dark:text-green-400',
    activeBg: 'bg-green-600 dark:bg-green-700',
    activeText: 'text-white',
  },
  {
    key: 'snoozed',
    label: 'Snoozed',
    icon: BellOff,
    bg: 'bg-zinc-100 dark:bg-zinc-800/40',
    border: 'border-zinc-200 dark:border-zinc-700',
    text: 'text-zinc-600 dark:text-zinc-400',
    activeBg: 'bg-zinc-600 dark:bg-zinc-600',
    activeText: 'text-white',
  },
];

export function MaintenanceSummaryCards({
  overdue,
  upcoming,
  completed,
  snoozed,
  activeFilter,
  onFilterChange,
}: Props) {
  const counts: Record<string, number> = { overdue, upcoming, completed, snoozed };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {cards.map((card) => {
        const Icon = card.icon;
        const isActive = activeFilter === card.key;
        const count = counts[card.key] ?? 0;

        return (
          <button
            key={card.key}
            onClick={() => onFilterChange(isActive ? 'all' : card.key)}
            className={cn(
              'relative flex flex-col items-start gap-1 rounded-xl border px-4 py-3 text-left transition-all duration-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive
                ? cn(card.activeBg, card.activeText, 'border-transparent shadow-sm')
                : cn(card.bg, card.border, card.text),
            )}
          >
            <Icon className="h-4 w-4" strokeWidth={2} />
            <span className="text-2xl font-bold leading-none">{count}</span>
            <span className="text-xs font-medium opacity-80">{card.label}</span>
          </button>
        );
      })}
    </div>
  );
}
